import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { ChevronLeft, Clock } from "lucide-react";

const TimelinePage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEvent, setActiveEvent] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const res = await axios.get(`${API}/timelines/${slug}`);
        setData(res.data);
        if (res.data.events?.length) setActiveEvent(0);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center"><div className="spinner" /></div>;

  if (!data?.events?.length) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No timeline data available for this universe yet.</p>
            <Link to={`/universe/${slug}`} className="text-amber-500 text-sm mt-4 inline-block hover:underline">Back to universe</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const eras = [...new Set(data.events.map(e => e.era).filter(Boolean))];
  const eraColors = {
    "Old Republic": "border-blue-500 bg-blue-500",
    "Prequel Era": "border-cyan-500 bg-cyan-500",
    "Clone Wars": "border-orange-500 bg-orange-500",
    "Imperial Era": "border-red-600 bg-red-600",
    "Rebellion": "border-green-500 bg-green-500",
    "Sequel Era": "border-purple-500 bg-purple-500",
    "World War II": "border-yellow-700 bg-yellow-700",
    "Pre-Avengers": "border-sky-500 bg-sky-500",
    "Iron Man Era": "border-red-500 bg-red-500",
    "Avengers Era": "border-blue-500 bg-blue-500",
    "Infinity Saga": "border-purple-600 bg-purple-600",
    "First Age": "border-emerald-500 bg-emerald-500",
    "Second Age": "border-teal-500 bg-teal-500",
    "Third Age": "border-amber-600 bg-amber-600",
    "War of the Ring": "border-red-500 bg-red-500",
    "Dawn Age": "border-stone-500 bg-stone-500",
    "Age of Heroes": "border-yellow-500 bg-yellow-500",
    "Targaryen": "border-red-600 bg-red-600",
    "Robert's Rebellion": "border-yellow-600 bg-yellow-600",
    "War of Five Kings": "border-orange-600 bg-orange-600",
    "Origins": "border-blue-500 bg-blue-500",
    "Formation": "border-green-500 bg-green-500",
    "Crisis Events": "border-red-500 bg-red-500",
    "Modern": "border-slate-400 bg-slate-400",
    "Pre-Federation": "border-cyan-500 bg-cyan-500",
    "Federation Founding": "border-blue-500 bg-blue-500",
    "TOS Era": "border-yellow-500 bg-yellow-500",
    "TNG Era": "border-teal-500 bg-teal-500",
    "Picard Era": "border-purple-500 bg-purple-500",
  };

  const getEraColor = (era) => eraColors[era] || "border-amber-500 bg-amber-500";

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title={`${data.universe} Timeline`} description={`Explore the complete timeline of the ${data.universe} universe.`} />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="mb-10">
          <Link to={`/universe/${slug}`} className="text-amber-500 text-sm flex items-center gap-1 mb-4 hover:underline">
            <ChevronLeft className="w-4 h-4" /> Back to {data.universe}
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-2">{data.universe} Timeline</h1>
          <p className="text-slate-400 text-sm">{data.events.length} events across {eras.length} eras</p>
          
          {/* Era Legend */}
          <div className="flex flex-wrap gap-2 mt-4">
            {eras.map((era) => (
              <span key={era} className="flex items-center gap-1.5 text-[10px] text-slate-400 font-mono">
                <span className={`w-2 h-2 rounded-full ${getEraColor(era).split(" ")[1]}`} />
                {era}
              </span>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="relative" data-testid="timeline-container">
          {/* Vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-amber-500/50 via-white/10 to-transparent" />
          
          <div className="space-y-0">
            {data.events.map((event, i) => {
              const isLeft = i % 2 === 0;
              const colorClass = getEraColor(event.era);
              const isActive = activeEvent === i;
              
              return (
                <div
                  key={event.id}
                  className={`relative flex items-start gap-4 md:gap-8 py-4 cursor-pointer group ${
                    isLeft ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  onClick={() => setActiveEvent(isActive ? null : i)}
                  data-testid={`timeline-event-${i}`}
                >
                  {/* Dot */}
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
                    <div className={`w-4 h-4 rounded-full border-2 ${colorClass.split(" ")[0]} ${
                      isActive ? colorClass.split(" ")[1] : "bg-[var(--bg-base)]"
                    } transition-colors shadow-lg`} />
                  </div>

                  {/* Content card */}
                  <div className={`ml-14 md:ml-0 md:w-[calc(50%-2rem)] ${isLeft ? "md:pr-8 md:text-right" : "md:pl-8"}`}>
                    <div className={`card-surface p-5 transition-all ${
                      isActive ? "border-amber-500/30 shadow-lg shadow-amber-500/5" : "hover:border-white/10"
                    }`}>
                      <div className={`flex items-center gap-2 mb-2 ${isLeft ? "md:justify-end" : ""}`}>
                        {event.era && (
                          <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${colorClass.split(" ")[1]}/20 text-white/70`}>
                            {event.era}
                          </span>
                        )}
                        <span className="text-xs font-mono text-amber-500">{event.year}</span>
                      </div>
                      <h3 className="text-white font-semibold font-['Outfit'] mb-1">{event.title}</h3>
                      <p className={`text-sm text-slate-400 leading-relaxed transition-all overflow-hidden ${
                        isActive ? "max-h-96 opacity-100" : "max-h-12 opacity-70"
                      }`}>
                        {event.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TimelinePage;
